import {IAdditionalInfo} from '../../../../models/additionalInfo';
import {ISiteType} from '../../../../models/siteType';
import {ISubstrateOption} from '../../../../models/substrateOption';
import {ICreateCleanUpForm} from '../index';
import s from '../index.module.scss';

function toDMS(decimal: number, isLat: boolean): string {
    const abs = Math.abs(decimal);
    const deg = Math.floor(abs);
    const minFloat = (abs - deg) * 60;
    const min = Math.floor(minFloat);
    const sec = ((minFloat - min) * 60).toFixed(2);
    const dir = isLat ? (decimal >= 0 ? 'N' : 'S') : decimal >= 0 ? 'E' : 'W';
    return `${deg}°${min}'${sec}"${dir}`;
}

interface Props {
    form: ICreateCleanUpForm;
    substrateOptions: ISubstrateOption[];
    siteTypes: ISiteType[];
    additionalInfo: IAdditionalInfo[];
    onChange: (updates: Partial<ICreateCleanUpForm>) => void;
    showErrors?: boolean;
}

export default function Step3Location({
    form,
    substrateOptions,
    siteTypes,
    additionalInfo,
    onChange,
    showErrors,
}: Props) {
    const toggleSiteType = (id: string) => {
        const updated = form.site_types.includes(id)
            ? form.site_types.filter((s) => s !== id)
            : [...form.site_types, id];
        onChange({site_types: updated});
    };

    const toggleAdditionalInfo = (id: string) => {
        const updated = form.additional_location_info.includes(id)
            ? form.additional_location_info.filter((s) => s !== id)
            : [...form.additional_location_info, id];
        onChange({additional_location_info: updated});
    };

    return (
        <div className={s.stepBody}>
            {/* Site Types */}
            {siteTypes.length > 0 && (
                <>
                    <div className={s.subSectionTitle}>Site Types</div>
                    <div className={s.checkboxGroup}>
                        {siteTypes.map((st) => (
                            <label key={st.id} className={s.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={form.site_types.includes(st.id)}
                                    onChange={() => toggleSiteType(st.id)}
                                />
                                {st.title}
                            </label>
                        ))}
                    </div>
                </>
            )}

            <div className={s.subSectionTitle}>Coordinates</div>

            <div className={s.twoCol}>
                <div className={s.formGroup}>
                    <label className={s.required}>Latitude</label>
                    <input
                        type="number"
                        step="0.000001"
                        value={form.latitude}
                        onChange={(e) => onChange({latitude: e.target.value})}
                        className={`${s.input}${showErrors && !form.latitude ? ` ${s.inputError}` : ''}`}
                        placeholder="e.g., 37.9838"
                    />
                    {showErrors && !form.latitude && (
                        <span className={s.fieldError}>Latitude is required</span>
                    )}
                    {form.latitude && (
                        <span className={s.coordNote}>
                            {toDMS(parseFloat(form.latitude), true)}
                        </span>
                    )}
                </div>
                <div className={s.formGroup}>
                    <label className={s.required}>Longitude</label>
                    <input
                        type="number"
                        step="0.000001"
                        value={form.longitude}
                        onChange={(e) => onChange({longitude: e.target.value})}
                        className={`${s.input}${showErrors && !form.longitude ? ` ${s.inputError}` : ''}`}
                        placeholder="e.g., 23.7275"
                    />
                    {showErrors && !form.longitude && (
                        <span className={s.fieldError}>Longitude is required</span>
                    )}
                    {form.longitude && (
                        <span className={s.coordNote}>
                            {toDMS(parseFloat(form.longitude), false)}
                        </span>
                    )}
                </div>
            </div>

            <div className={s.subSectionTitle}>Region</div>

            <div className={s.formGroup}>
                <label>Location Name</label>
                <input
                    type="text"
                    value={form.location_name}
                    onChange={(e) => onChange({location_name: e.target.value})}
                    className={s.input}
                    placeholder="e.g., North Beach Area"
                />
            </div>

            <div className={s.twoCol}>
                <div className={s.formGroup}>
                    <label>District</label>
                    <input
                        type="text"
                        value={form.district}
                        onChange={(e) => onChange({district: e.target.value})}
                        className={s.input}
                    />
                </div>
                <div className={s.formGroup}>
                    <label>Municipality</label>
                    <input
                        type="text"
                        value={form.municipality}
                        onChange={(e) => onChange({municipality: e.target.value})}
                        className={s.input}
                    />
                </div>
            </div>

            <div className={s.formGroup}>
                <label>City / Island</label>
                <input
                    type="text"
                    value={form.city_island}
                    onChange={(e) => onChange({city_island: e.target.value})}
                    className={s.input}
                />
            </div>

            <div className={s.subSectionTitle}>Morphology</div>

            <div className={s.twoCol}>
                <div className={s.formGroup}>
                    <label>Area (sqm)</label>
                    <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={form.area}
                        onChange={(e) => onChange({area: e.target.value})}
                        className={s.input}
                        placeholder="e.g., 500"
                    />
                </div>
                <div className={s.formGroup}>
                    <label>Length (m)</label>
                    <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={form.length}
                        onChange={(e) => onChange({length: e.target.value})}
                        className={s.input}
                        placeholder="e.g., 200"
                    />
                </div>
            </div>

            <div className={s.formGroup}>
                <label>Substrate</label>
                <select
                    value={form.substrate_id}
                    onChange={(e) => onChange({substrate_id: e.target.value})}
                    className={s.select}
                >
                    <option value="">Select substrate</option>
                    {substrateOptions.map((so) => (
                        <option key={so.id} value={so.id}>
                            {so.title}
                        </option>
                    ))}
                </select>
            </div>

            {/* Additional Location Info */}
            {additionalInfo.length > 0 && (
                <>
                    <div className={s.subSectionTitle}>Additional Location Info</div>
                    <div className={s.checkboxGroup}>
                        {additionalInfo.map((ai) => (
                            <label key={ai.id} className={s.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={form.additional_location_info.includes(ai.id)}
                                    onChange={() => toggleAdditionalInfo(ai.id)}
                                />
                                {ai.title}
                            </label>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
